// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Users, Mail, Phone, MapPin, GraduationCap, Trophy, Share2, Facebook, Twitter, Linkedin, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

interface TeamData {
  id: string;
  name: string;
  logo_url?: string;
  sport_name?: string;
  season?: string;
  age_group?: string;
  head_coach_name?: string;
  head_coach_email?: string;
  head_coach_cell?: string;
  assistant_coach_name?: string;
  assistant_coach_email?: string;
  assistant_coach_cell?: string;
  manager_name?: string;
  manager_email?: string;
  manager_cell?: string;
  public_view_count: number;
}

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  position?: string;
  grad_year?: string;
  jersey_number?: string;
  email?: string;
  profile_id?: string;
  avatar_url?: string;
}

const PublicTeamPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [roster, setRoster] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      fetchTeamData();
    }
  }, [slug]);

  const fetchTeamData = async () => {
    if (!slug) return;

    try {
      setLoading(true);
      setError(null);

      const { data: team, error: teamError } = await supabase
        .from('coach_teams')
        .select(`
          id,
          name,
          logo_url,
          season,
          age_group,
          head_coach_name,
          head_coach_email,
          head_coach_cell,
          assistant_coach_name,
          assistant_coach_email,
          assistant_coach_cell,
          manager_name,
          manager_email,
          manager_cell,
          public_view_count,
          sport:sports(name)
        `)
        .eq('slug', slug)
        .eq('is_public', true)
        .maybeSingle();

      if (teamError) throw teamError;
      if (!team) throw new Error('Team not found');

      await supabase
        .from('coach_teams')
        .update({ public_view_count: (team.public_view_count || 0) + 1 })
        .eq('id', team.id);

      const { data: rosterData, error: rosterError } = await supabase
        .from('coach_team_rosters')
        .select('*')
        .eq('team_id', team.id)
        .order('last_name');

      if (rosterError) throw rosterError;

      setTeamData({
        ...team,
        sport_name: Array.isArray(team.sport) ? team.sport[0]?.name : (team.sport as any)?.name || undefined,
      });
      setRoster(rosterData || []);
    } catch (err: any) {
      console.error('Error fetching team:', err);
      setError(err.message || 'Team not found or not publicly accessible');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-lg">Loading team...</div>
      </div>
    );
  }

  if (error || !teamData) {
    return (
      <div className="flex items-center justify-center py-20">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Team Not Found</h2>
            <p className="text-muted-foreground">
              This team page is not available or has been made private.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = `${teamData.name} - Team Roster`;
  const shareDescription = `Check out ${teamData.name}${teamData.sport_name ? ` ${teamData.sport_name}` : ''} team roster${teamData.age_group ? ` - ${teamData.age_group}` : ''} on RecruitLook`;

  const handleShare = async (platform: string) => {
    const encodedUrl = encodeURIComponent(currentUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedDescription = encodeURIComponent(shareDescription);

    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(currentUrl);
          toast.success('Link copied to clipboard!');
          return;
        } catch (err) {
          toast.error('Failed to copy link');
          return;
        }
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="bg-background py-8 px-4">
      <Helmet>
        <title>{shareTitle}</title>
        <meta name="description" content={shareDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        {teamData?.logo_url && <meta property="og:image" content={teamData.logo_url} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDescription} />
        {teamData?.logo_url && <meta name="twitter:image" content={teamData.logo_url} />}
      </Helmet>

      <div className="max-w-6xl mx-auto space-y-6">
        <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {teamData.logo_url && (
                  <img
                    src={teamData.logo_url}
                    alt={`${teamData.name} logo`}
                    className="w-20 h-20 object-cover rounded-lg border"
                  />
                )}
                <div className="flex-1">
                  <CardTitle className="text-3xl mb-2">{teamData.name}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    {teamData.sport_name && (
                      <Badge variant="secondary" className="text-sm">
                        {teamData.sport_name}
                      </Badge>
                    )}
                    {teamData.season && (
                      <Badge variant="outline" className="text-sm">
                        {teamData.season}
                      </Badge>
                    )}
                    {teamData.age_group && (
                      <Badge variant="outline" className="text-sm">
                        {teamData.age_group}
                      </Badge>
                    )}
                  </div>
                </div>
                
                {/* Social Share Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('facebook')}
                    className="gap-2"
                  >
                    <Facebook className="h-4 w-4" />
                    <span className="hidden sm:inline">Share</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('twitter')}
                    className="gap-2"
                  >
                    <Twitter className="h-4 w-4" />
                    <span className="hidden sm:inline">Tweet</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('linkedin')}
                    className="gap-2"
                  >
                    <Linkedin className="h-4 w-4" />
                    <span className="hidden sm:inline">Share</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('whatsapp')}
                    className="gap-2"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span className="hidden sm:inline">Share</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleShare('copy')}
                    className="gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Copy</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Coaching Staff</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!teamData.head_coach_name && !teamData.assistant_coach_name && !teamData.manager_name ? (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No coaching staff information available</p>
                </div>
              ) : (
                <>
                  {teamData.head_coach_name && (
                    <div className="border-l-4 border-primary pl-4">
                      <p className="font-semibold text-lg">{teamData.head_coach_name}</p>
                      <p className="text-sm text-muted-foreground mb-2">Head Coach</p>
                      <div className="space-y-1">
                        {teamData.head_coach_email && (
                          <a href={`mailto:${teamData.head_coach_email}`} className="flex items-center gap-2 text-sm hover:text-primary">
                            <Mail className="h-4 w-4" />
                            {teamData.head_coach_email}
                          </a>
                        )}
                        {teamData.head_coach_cell && (
                          <a href={`tel:${teamData.head_coach_cell}`} className="flex items-center gap-2 text-sm hover:text-primary">
                            <Phone className="h-4 w-4" />
                            {teamData.head_coach_cell}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {teamData.assistant_coach_name && (
                    <div className="border-l-4 border-muted pl-4">
                      <p className="font-semibold">{teamData.assistant_coach_name}</p>
                      <p className="text-sm text-muted-foreground mb-2">Assistant Coach</p>
                      <div className="space-y-1">
                        {teamData.assistant_coach_email && (
                          <a href={`mailto:${teamData.assistant_coach_email}`} className="flex items-center gap-2 text-sm hover:text-primary">
                            <Mail className="h-4 w-4" />
                            {teamData.assistant_coach_email}
                          </a>
                        )}
                        {teamData.assistant_coach_cell && (
                          <a href={`tel:${teamData.assistant_coach_cell}`} className="flex items-center gap-2 text-sm hover:text-primary">
                            <Phone className="h-4 w-4" />
                            {teamData.assistant_coach_cell}
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {teamData.manager_name && (
                    <div className="border-l-4 border-muted pl-4">
                      <p className="font-semibold">{teamData.manager_name}</p>
                      <p className="text-sm text-muted-foreground mb-2">Team Manager</p>
                      <div className="space-y-1">
                        {teamData.manager_email && (
                          <a href={`mailto:${teamData.manager_email}`} className="flex items-center gap-2 text-sm hover:text-primary">
                            <Mail className="h-4 w-4" />
                            {teamData.manager_email}
                          </a>
                        )}
                        {teamData.manager_cell && (
                          <a href={`tel:${teamData.manager_cell}`} className="flex items-center gap-2 text-sm hover:text-primary">
                            <Phone className="h-4 w-4" />
                            {teamData.manager_cell}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Roster ({roster.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {roster.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {roster.map((player) => {
                    const hasProfile = Boolean(player.profile_id);
                    
                    const cardContent = (
                      <Card className="hover:border-primary hover:shadow-md transition-all cursor-pointer h-full">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={player.avatar_url || undefined} />
                              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                                {player.first_name?.[0] || '?'}{player.last_name?.[0] || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold truncate">
                                  {player.first_name} {player.last_name}
                                </p>
                                {player.jersey_number && (
                                  <Badge variant="outline" className="text-xs">
                                    #{player.jersey_number}
                                  </Badge>
                                )}
                              </div>
                              <div className="space-y-1 mt-1">
                                {player.position && (
                                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {player.position}
                                  </p>
                                )}
                                {player.grad_year && (
                                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                                    <GraduationCap className="h-3 w-3" />
                                    Class of {player.grad_year}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                    
                    return hasProfile ? (
                      <a key={player.id} href={`/profile/${player.profile_id}`} className="block">
                        {cardContent}
                      </a>
                    ) : (
                      <div key={player.id}>
                        {cardContent}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No roster members added yet</p>
                </div>
              )}
            </CardContent>
          </Card>

        <div className="text-center text-sm text-muted-foreground pt-4">
          <p>Powered by <span className="font-semibold text-primary">RecruitLook</span></p>
        </div>
      </div>
    </div>
  );
};

export default PublicTeamPage;